import api from './authService';

export interface Todo {
  id: number;
  title: string;
  description?: string;
  created_by: number;
  assigned_to: number;
  assign_to_all?: boolean | number;
  show_on_calendar?: boolean | number;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  due_date?: string;
  reminder_date?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  creator_first_name?: string;
  creator_last_name?: string;
  creator_email?: string;
  assignee_first_name?: string;
  assignee_last_name?: string;
  assignee_email?: string;
  assignee_role?: string;
  store_name?: string;
  creator_store_name?: string;
  comments?: TodoComment[];
  attachments?: TodoAttachment[];
}

export interface TodoComment {
  id: number;
  todo_id: number;
  user_id: number;
  comment: string;
  created_at: string;
  first_name?: string;
  last_name?: string;
  role?: string;
}

export interface TodoAttachment {
  id: number;
  todo_id: number;
  file_name: string;
  file_path: string;
  file_size?: number;
  file_type?: string;
  uploaded_by: number;
  created_at: string;
  uploaded_by_first_name?: string;
  uploaded_by_last_name?: string;
}

export interface CreateTodoData {
  title: string;
  description?: string;
  assigned_to: number;
  assign_to_all?: boolean;
  show_on_calendar?: boolean;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  due_date?: string;
  reminder_date?: string;
  is_recurring?: boolean;
  recurrence_pattern?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  recurrence_day?: string;
  recurrence_end_date?: string;
}

export interface CreateCalendarTodoData {
  title: string;
  description?: string;
  assigned_to: number;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  due_date: string;
  due_time?: string;
  reminder_date?: string;
}

export interface UpdateTodoData {
  title?: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  status?: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  due_date?: string;
  reminder_date?: string;
}

export interface AssignableUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  store_name?: string;
  position?: string;
}

export interface PaginationMeta {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface TodoStats {
  pending: number;
  inProgress: number;
  completed: number;
  overdue: number;
}

export interface TodoResponse {
  data: Todo[];
  pagination?: PaginationMeta;
  stats?: TodoStats;
}

export const todoService = {
  // Get all todos (with optional pagination and status filter)
  async getTodos(page?: number, limit?: number, status?: string): Promise<TodoResponse> {
    try {
      // Build URL with query params
      const params = new URLSearchParams();
      if (page !== undefined) {
        params.append('page', page.toString());
      }
      if (limit !== undefined) {
        params.append('limit', limit.toString());
      }
      if (status) {
        params.append('status', status);
      }
      
      const url = params.toString() ? `/todos?${params.toString()}` : '/todos';
      const response = await api.get(url);
      return {
        data: response.data.data,
        pagination: response.data.pagination,
        stats: response.data.stats
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch todos');
    }
  },

  // Get todo by ID
  async getTodoById(id: number): Promise<Todo> {
    try {
      const response = await api.get(`/todos/${id}`);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch todo');
    }
  },

  // Create new todo (admin only)
  async createTodo(todoData: CreateTodoData): Promise<Todo> {
    try {
      const response = await api.post('/todos', todoData);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create todo');
    }
  },

  // Update todo
  async updateTodo(id: number, todoData: UpdateTodoData): Promise<Todo> {
    try {
      const response = await api.put(`/todos/${id}`, todoData);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update todo');
    }
  },

  // Delete todo (admin only)
  async deleteTodo(id: number): Promise<void> {
    try {
      await api.delete(`/todos/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete todo');
    }
  },

  // Add comment to todo
  async addComment(todoId: number, comment: string): Promise<TodoComment> {
    try {
      const response = await api.post(`/todos/${todoId}/comments`, { comment });
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to add comment');
    }
  },

  // Get assignable users (admin only)
  async getAssignableUsers(): Promise<AssignableUser[]> {
    try {
      const response = await api.get('/todos/assignable-users');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch assignable users');
    }
  },

  // Get todos for calendar view
  async getTodosForCalendar(startDate: string, endDate: string): Promise<Record<string, Todo[]>> {
    try {
      const response = await api.get(`/todos/calendar?startDate=${startDate}&endDate=${endDate}`);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch calendar todos');
    }
  },

  // Create calendar todo (admin only)
  async createCalendarTodo(todoData: CreateCalendarTodoData): Promise<Todo> {
    try {
      const response = await api.post('/todos/calendar', todoData);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create calendar todo');
    }
  }
};
